import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { showConfirm } from '@/lib/swal'
import { companyBanksService } from '@/services/companyBanksService'
import type { CompanyBankListItem } from '@/api/types'

export const CompanyBanksList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const branchId = user?.selectedBranch?.branchId

  const [rows, setRows] = useState<CompanyBankListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageSize, setPageSize] = useState(10)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize))

  useEffect(() => {
    if (!branchId) return

    const load = async () => {
      try {
        setLoading(true)
        const res = await companyBanksService.getAll(branchId, pageNumber, pageSize)
        setRows(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (e: any) {
        const status = e?.response?.status
        const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
        const isNoData = status === 404 || /no data|not found|no records/.test(msg)
        setRows([])
        setTotalRecords(0)
        if (!isNoData) toast.error(e?.response?.data?.message ?? t('companyBanks.fetchFailed'))
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [branchId, pageNumber, pageSize, t])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter((r) => {
      const nameEn = (r.bankNameEn ?? '').toLowerCase()
      const nameAr = (r.bankNameAr ?? '').toLowerCase()
      const person = (r.primaryPerson ?? '').toLowerCase()
      const email = (r.email ?? '').toLowerCase()
      const phone = (r.phoneNo ?? '').toLowerCase()
      return nameEn.includes(q) || nameAr.includes(q) || person.includes(q) || email.includes(q) || phone.includes(q)
    })
  }, [rows, searchQuery])

  const handleAdd = () => navigate('/company-banks/new')
  const handleEdit = (id: number) => navigate(`/company-banks/${id}/edit`)

  const handleDelete = async (id: number) => {
    const ok = await showConfirm(t('companyBanks.deleteTitle'), t('companyBanks.deleteConfirm'), 'warning')
    if (!ok) return
    try {
      setLoading(true)
      await companyBanksService.delete(id)
      toast.success(t('companyBanks.deleted'))
      if (branchId) {
        const res = await companyBanksService.getAll(branchId, pageNumber, pageSize)
        setRows(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('companyBanks.deleteFailed'))
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('companyBanks.title')} description={t('companyBanks.description')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState title={t('banks.branchNotSelected')} description={t('banks.selectBranchToView')} />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('companyBanks.title')}
        description={t('companyBanks.description')}
        action={{ label: t('companyBanks.addNew'), onClick: handleAdd, icon: Plus }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder={t('companyBanks.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPageNumber(1)
                }}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">{t('companyBanks.pageSize')}</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent searchable={false}>
                  {[10, 20, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('companyBanks.loading')}</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={t('companyBanks.noData')}
                description={searchQuery ? t('companyBanks.tryAdjustingSearch') : t('companyBanks.getStarted')}
                action={!searchQuery ? { label: t('companyBanks.addNew'), onClick: handleAdd } : undefined}
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('companyBanks.bankName')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('companyBanks.primaryPerson')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('companyBanks.email')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('companyBanks.phoneNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('companyBanks.status')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[110px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('companyBanks.action')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.map((r) => (
                      <TableRow key={r.id} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {r.bankNameEn || r.bankNameAr || '—'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.primaryPerson ?? '—'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.email ?? '—'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{r.phoneNo ?? '—'}</TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {r.activeStatus ? t('companyBanks.active') : t('companyBanks.inactive')}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(Number(r.id))}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(Number(r.id))}
                              className="h-7 w-7 p-0 hover:bg-destructive/10 dark:hover:bg-destructive/20 text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="border-t px-6 py-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="text-sm text-muted-foreground">
                    {t('companyBanks.showing')} {(pageNumber - 1) * pageSize + 1} {t('companyBanks.to')}{' '}
                    {Math.min(pageNumber * pageSize, totalRecords)} {t('companyBanks.of')} {totalRecords}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                      disabled={pageNumber <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('companyBanks.previous')}
                    </Button>
                    <div className="text-sm">
                      {t('companyBanks.pageOf', { page: pageNumber, totalPages })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber((p) => p + 1)}
                      disabled={pageNumber >= totalPages}
                    >
                      {t('companyBanks.next')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

